import type { ApiRoute, ApiWaypoint } from '@/types/api'

const DB_NAME = 'logbook'
const DB_VERSION = 1
const ROUTES_STORE = 'routes'
const WAYPOINTS_STORE = 'waypoints'

let dbInstance: IDBDatabase | null = null

function openDatabase(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance)
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      reject(new Error('Failed to open database'))
    }

    request.onsuccess = () => {
      dbInstance = request.result
      resolve(dbInstance)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Routes store
      if (!db.objectStoreNames.contains(ROUTES_STORE)) {
        const routesStore = db.createObjectStore(ROUTES_STORE, { keyPath: 'id' })
        routesStore.createIndex('user_id', 'user_id', { unique: false })
        routesStore.createIndex('slug', 'slug', { unique: false })
        routesStore.createIndex('is_deleted', 'is_deleted', { unique: false })
        routesStore.createIndex('last_update', 'last_update', { unique: false })
      }

      // Waypoints store
      if (!db.objectStoreNames.contains(WAYPOINTS_STORE)) {
        const waypointsStore = db.createObjectStore(WAYPOINTS_STORE, { keyPath: 'id' })
        waypointsStore.createIndex('route_id', 'route_id', { unique: false })
        waypointsStore.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }
  })
}

// Route stored without waypoints (waypoints stored separately)
type StoredRoute = Omit<ApiRoute, 'waypoints'>

async function getAllRoutes(): Promise<ApiRoute[]> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([ROUTES_STORE, WAYPOINTS_STORE], 'readonly')
    const routesStore = transaction.objectStore(ROUTES_STORE)
    const waypointsStore = transaction.objectStore(WAYPOINTS_STORE)

    const routesRequest = routesStore.getAll()

    routesRequest.onerror = () => reject(new Error('Failed to get routes'))

    routesRequest.onsuccess = () => {
      const storedRoutes: StoredRoute[] = routesRequest.result
      const waypointsRequest = waypointsStore.getAll()

      waypointsRequest.onerror = () => reject(new Error('Failed to get waypoints'))

      waypointsRequest.onsuccess = () => {
        const allWaypoints: ApiWaypoint[] = waypointsRequest.result

        // Group waypoints by route_id
        const waypointsByRoute = new Map<string, ApiWaypoint[]>()
        for (const wp of allWaypoints) {
          const existing = waypointsByRoute.get(wp.route_id) || []
          existing.push(wp)
          waypointsByRoute.set(wp.route_id, existing)
        }

        // Combine routes with their waypoints
        const routes: ApiRoute[] = storedRoutes.map((route) => ({
          ...route,
          waypoints: waypointsByRoute.get(route.id) || []
        }))

        resolve(routes)
      }
    }
  })
}

async function getRouteById(routeId: string): Promise<ApiRoute | null> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([ROUTES_STORE, WAYPOINTS_STORE], 'readonly')
    const routesStore = transaction.objectStore(ROUTES_STORE)
    const waypointsStore = transaction.objectStore(WAYPOINTS_STORE)

    const routeRequest = routesStore.get(routeId)

    routeRequest.onerror = () => reject(new Error('Failed to get route'))

    routeRequest.onsuccess = () => {
      const storedRoute: StoredRoute | undefined = routeRequest.result
      if (!storedRoute) {
        resolve(null)
        return
      }

      const waypointsIndex = waypointsStore.index('route_id')
      const waypointsRequest = waypointsIndex.getAll(routeId)

      waypointsRequest.onerror = () => reject(new Error('Failed to get waypoints'))

      waypointsRequest.onsuccess = () => {
        const waypoints: ApiWaypoint[] = waypointsRequest.result
        resolve({
          ...storedRoute,
          waypoints
        })
      }
    }
  })
}

async function saveRoute(route: ApiRoute): Promise<void> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([ROUTES_STORE, WAYPOINTS_STORE], 'readwrite')
    const routesStore = transaction.objectStore(ROUTES_STORE)
    const waypointsStore = transaction.objectStore(WAYPOINTS_STORE)

    // Store route without waypoints
    const { waypoints, ...routeWithoutWaypoints } = route
    const routeRequest = routesStore.put(routeWithoutWaypoints)

    routeRequest.onerror = () => reject(new Error('Failed to save route'))

    routeRequest.onsuccess = () => {
      // Delete existing waypoints for this route, then add new ones
      const waypointsIndex = waypointsStore.index('route_id')
      const existingRequest = waypointsIndex.getAllKeys(route.id)

      existingRequest.onsuccess = () => {
        const existingKeys = existingRequest.result
        for (const key of existingKeys) {
          waypointsStore.delete(key)
        }

        // Add all waypoints (serialize to remove circular references and Vue proxies)
        for (const waypoint of waypoints) {
          // Use JSON serialization to create a plain object without Vue reactivity
          const waypointData = JSON.parse(JSON.stringify(waypoint))
          waypointsStore.put(waypointData)
        }
      }

      existingRequest.onerror = () => {
        // Still try to add waypoints even if delete fails
        for (const waypoint of waypoints) {
          const waypointData = JSON.parse(JSON.stringify(waypoint))
          waypointsStore.put(waypointData)
        }
      }
    }

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(new Error('Transaction failed'))
  })
}

async function saveRoutes(routes: ApiRoute[]): Promise<void> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([ROUTES_STORE, WAYPOINTS_STORE], 'readwrite')
    const routesStore = transaction.objectStore(ROUTES_STORE)
    const waypointsStore = transaction.objectStore(WAYPOINTS_STORE)

    // Clear existing data
    routesStore.clear()
    waypointsStore.clear()

    // Add all routes and waypoints
    for (const route of routes) {
      const { waypoints, ...routeWithoutWaypoints } = route
      routesStore.put(routeWithoutWaypoints)

      for (const waypoint of waypoints) {
        waypointsStore.put(waypoint)
      }
    }

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(new Error('Transaction failed'))
  })
}

async function deleteRoute(routeId: string): Promise<void> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([ROUTES_STORE, WAYPOINTS_STORE], 'readwrite')
    const routesStore = transaction.objectStore(ROUTES_STORE)
    const waypointsStore = transaction.objectStore(WAYPOINTS_STORE)

    // Delete route
    routesStore.delete(routeId)

    // Delete associated waypoints
    const waypointsIndex = waypointsStore.index('route_id')
    const waypointsRequest = waypointsIndex.getAllKeys(routeId)

    waypointsRequest.onsuccess = () => {
      const keys = waypointsRequest.result
      for (const key of keys) {
        waypointsStore.delete(key)
      }
    }

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(new Error('Transaction failed'))
  })
}

async function saveWaypoint(waypoint: ApiWaypoint): Promise<void> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(WAYPOINTS_STORE, 'readwrite')
    const store = transaction.objectStore(WAYPOINTS_STORE)

    const request = store.put(waypoint)

    request.onerror = () => reject(new Error('Failed to save waypoint'))
    transaction.oncomplete = () => resolve()
  })
}

async function deleteWaypoint(waypointId: string): Promise<void> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(WAYPOINTS_STORE, 'readwrite')
    const store = transaction.objectStore(WAYPOINTS_STORE)

    const request = store.delete(waypointId)

    request.onerror = () => reject(new Error('Failed to delete waypoint'))
    transaction.oncomplete = () => resolve()
  })
}

async function clearAllData(): Promise<void> {
  const db = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([ROUTES_STORE, WAYPOINTS_STORE], 'readwrite')
    const routesStore = transaction.objectStore(ROUTES_STORE)
    const waypointsStore = transaction.objectStore(WAYPOINTS_STORE)

    routesStore.clear()
    waypointsStore.clear()

    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(new Error('Transaction failed'))
  })
}

export const localDatabase = {
  getAllRoutes,
  getRouteById,
  saveRoute,
  saveRoutes,
  deleteRoute,
  saveWaypoint,
  deleteWaypoint,
  clearAllData
}
